// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Stat Cards Component
// KPI cards with deltas
// ═══════════════════════════════════════════════════════════════════════════════

import { TrendingUp, TrendingDown, MessageSquare, UserCheck, Target, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SparklineChart } from '@/components/ui/sparkline';
import { cn } from '@/lib/utils';
import { formatUiNumber } from '@/lib/ui-format';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface StatCardsProps {
  kpis: {
    totalSessions: number;
    leadsCapured: number;
    conversionRate: number;
    avgQualificationScore: number;
    costPerLead: number;
    totalCost: number;
    sessionsDelta: number | null;
    conversionDelta: number | null;
    scoreDelta: number | null;
    costDelta: number | null;
  };
  trends?: Array<{
    sessions: number;
    leads: number;
    conversionRate: number;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const SPARKLINE_COLORS = {
  blue: 'var(--color-status-info)',
  green: 'var(--color-status-success)',
  orange: 'var(--color-status-warning)',
  purple: 'var(--color-brand-500)',
} as const;

export function StatCards({ kpis, trends }: StatCardsProps): JSX.Element {
  const sessionsTrend = trends?.map((item) => item.sessions) || [];
  const leadsTrend = trends?.map((item) => item.leads) || [];
  const conversionTrend = trends?.map((item) => item.conversionRate) || [];

  const stats = [
    {
      label: 'Sessions',
      value: kpis.totalSessions,
      icon: MessageSquare,
      color: 'blue',
      delta: kpis.sessionsDelta,
      suffix: '',
      trendData: sessionsTrend,
    },
    {
      label: 'Leads',
      value: kpis.leadsCapured,
      icon: UserCheck,
      color: 'green',
      delta: kpis.sessionsDelta,
      suffix: '',
      trendData: leadsTrend,
    },
    {
      label: 'Conv Rate',
      value: kpis.conversionRate,
      icon: Target,
      color: 'orange',
      delta: kpis.conversionDelta,
      suffix: '%',
      isDeltaAbsolute: true,
      trendData: conversionTrend,
    },
    {
      label: 'Cost/Lead',
      value: kpis.costPerLead,
      icon: DollarSign,
      color: 'purple',
      delta: kpis.costDelta,
      suffix: '',
      prefix: '$',
      isInverted: true,
      trendData: [],
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat) => {
        const deltaValue = stat.delta ?? 0;
        const isImproving = stat.isInverted ? deltaValue < 0 : deltaValue > 0;
        const deltaClassName = isImproving
          ? 'bg-status-success/15 text-status-success'
          : 'bg-status-danger/15 text-status-danger';
        let DeltaIcon = TrendingDown;
        if (stat.isInverted) {
          DeltaIcon = deltaValue < 0 ? TrendingDown : TrendingUp;
        } else {
          DeltaIcon = deltaValue > 0 ? TrendingUp : TrendingDown;
        }

        return (
          <Card
            key={stat.label}
          className={cn(
            'p-6 transition-all hover:-translate-y-1 hover:shadow-elevation-2',
            'border-l-4',
            stat.color === 'blue' && 'border-l-status-info',
            stat.color === 'green' && 'border-l-status-success',
            stat.color === 'orange' && 'border-l-status-warning',
            stat.color === 'purple' && 'border-l-primary'
          )}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                stat.color === 'blue' && 'bg-status-info/10',
                stat.color === 'green' && 'bg-status-success/10',
                stat.color === 'orange' && 'bg-status-warning/10',
                stat.color === 'purple' && 'bg-primary/10'
              )}
            >
              <stat.icon
                className={cn(
                  'w-5 h-5',
                  stat.color === 'blue' && 'text-status-info',
                  stat.color === 'green' && 'text-status-success',
                  stat.color === 'orange' && 'text-status-warning',
                  stat.color === 'purple' && 'text-primary'
                )}
              />
            </div>

            {stat.delta !== null && stat.delta !== 0 && (
              <div
                className={cn(
                  'flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
                  deltaClassName
                )}
              >
                <DeltaIcon className="w-3 h-3" />
                {stat.isDeltaAbsolute
                  ? `${stat.delta > 0 ? '+' : ''}${stat.delta.toFixed(1)}pp`
                  : `${stat.delta > 0 ? '+' : ''}${stat.delta}%`}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-3xl font-bold text-foreground">
              {stat.prefix}
              {formatUiNumber(stat.value)}
              {stat.suffix}
            </p>
            <p className="text-sm text-muted-foreground">{stat.label}</p>
          </div>

          {stat.trendData.length > 1 && (
            <div className="mt-4">
              <SparklineChart
                data={stat.trendData}
                width={140}
                height={36}
                color={SPARKLINE_COLORS[stat.color as keyof typeof SPARKLINE_COLORS]}
              />
            </div>
          )}
        </Card>
        );
      })}
    </div>
  );
}
